import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { OptionChainViewKey, OptionChainViewType } from '../assets/constants'
import AnalyticsManager from '../analytics/AnalyticsManager'
import { PrimarySecurity } from '../util/security'

export interface HomeStateType {
    primarySecurity: {
        key: string,
        displayName?: string,
        expressionMode: boolean,
        securities: string[],
        underlyingSecurityCode: string
    },
    resolvedPrimarySecurity: PrimarySecurity | null,
    fnoView: {
        selectedOptionsExpiry: string,
    },
    optionChain: Record<OptionChainViewKey, {
        view: OptionChainViewType
        expandedRows: string[]
    }>,
    activeLayout: string,
    lastActiveLayout: string,
    securitySearchOpen: {
        open: boolean,
        key: string | undefined,
        isExpression: boolean,
        onSecuritySelected?: (key: string, expressionMode: boolean, securities: string[]) => void
    }
    financialPanelOpen: boolean
    financialPanelSection: string | null
}

export const homeInitialState: HomeStateType = {
    primarySecurity: {
        key: '',
        expressionMode: false,
        securities: [],
        underlyingSecurityCode: ''
    },
    resolvedPrimarySecurity: null,
    fnoView: {
        selectedOptionsExpiry: ''
    },
    optionChain: {
        [OptionChainViewKey.LIVE]: {
            view: OptionChainViewType.TABLE,
            expandedRows: ['0']
        },
        [OptionChainViewKey.HISTORICAL]: {
            view: OptionChainViewType.TABLE,
            expandedRows: ['0']
        },
        [OptionChainViewKey.WIDGET]: {
            view: OptionChainViewType.TABLE,
            expandedRows: ['0']
        }
    },
    activeLayout: '',
    lastActiveLayout: '',
    securitySearchOpen: {
        open: false,
        key: undefined,
        isExpression: false, 
    },
    financialPanelOpen: false,
    financialPanelSection: null,
}

const homeSlice = createSlice({
    name: 'home',
    initialState: homeInitialState,
    reducers: {
        updatePrimarySecurity(state: HomeStateType, action: PayloadAction<HomeStateType['primarySecurity']>) {
            const { displayName, ...rest } = action.payload
            return {
                ...state,
                primarySecurity: {
                    ...state.primarySecurity,
                    ...rest,
                    // Preserve displayName when not provided to avoid clearing it on other panes (e.g. when widget type changes)
                    ...(displayName !== undefined ? { displayName } : {})
                },
                resolvedPrimarySecurity: null
            }
        },
        setResolvedPrimarySecurity(state: HomeStateType, action: PayloadAction<PrimarySecurity>) {
            state.resolvedPrimarySecurity = action.payload
        },
        setOptionChainExpandedRows(state: HomeStateType, action: PayloadAction<{
            expandedRows: string[]
            optionChainViewKey: OptionChainViewKey}
        >) {
            const existing = state.optionChain[action.payload.optionChainViewKey] ?? {
                view: OptionChainViewType.TABLE,
                expandedRows: ['0']
            }
            return {
                ...state,
                optionChain: {
                    ...state.optionChain,
                    [action.payload.optionChainViewKey]: {
                        ...existing,
                        expandedRows: action.payload.expandedRows
                    }
                }
            }
        },
        toggleOptionChainExpandedRow(state: HomeStateType, action: PayloadAction<{
                key: string,
                expirationDate: string,
                optionChainViewKey: OptionChainViewKey
            }>) {
            const expandedRows = [...state.optionChain[action.payload.optionChainViewKey].expandedRows]
            if(!expandedRows.includes(action.payload.key)) {
                expandedRows.push(action.payload.key)
                expandedRows.sort((a, b) => Number(a) - Number(b))
                AnalyticsManager.logEvent('Bar Expanded', { 'expiry': action.payload.expirationDate, source: 'option-chain-table' })
            } else {
                const index = expandedRows.indexOf(action.payload.key)
                expandedRows.splice(index, 1)
                AnalyticsManager.logEvent('Bar Collapsed', { 'expiry': action.payload.expirationDate, source: 'option-chain-table' })
            }
            return {
                ...state,
                optionChain: {
                    ...state.optionChain,
                    [action.payload.optionChainViewKey]: {
                        ...state.optionChain[action.payload.optionChainViewKey],
                        expandedRows: expandedRows
                    }
                }
            }
        },
        setOptionChainView(state: HomeStateType, action: PayloadAction<{
            view: OptionChainViewType,
            optionChainViewKey: OptionChainViewKey
        }>) {
            return {
                ...state,
                optionChain: {
                    ...state.optionChain,
                    [action.payload.optionChainViewKey]: {
                        ...state.optionChain[action.payload.optionChainViewKey],
                        view: action.payload.view
                    }
                }
            }
        },
        setSelectedOptionsExpiry(state: HomeStateType, action: PayloadAction<string>) {
            return {
                ...state,
                fnoView: {
                    ...state.fnoView,
                    selectedOptionsExpiry: action.payload
                }
            }
        },
        setActiveLayout(state, action: PayloadAction<string>) {
            return {
                ...state,
                activeLayout: action.payload
            }
        },
        setLastActiveLayout(state, action: PayloadAction<string>) {
            return {
                ...state,
                lastActiveLayout: action.payload
            }
        },
        setSecuritySearchOpen(state, action: PayloadAction<HomeStateType['securitySearchOpen']>) {
            return {
                ...state,
                securitySearchOpen: {
                    ...action.payload
                }
            }
        },
        setFinancialPanelOpen(state, action: PayloadAction<boolean | { open: boolean; section?: string }>) {
            const payload = action.payload
            const open = typeof payload === 'boolean' ? payload : payload.open
            state.financialPanelOpen = open
            if (!open) {
                state.financialPanelSection = null
            } else if (typeof payload === 'object' && payload.section) {
                state.financialPanelSection = payload.section
            } else if (state.financialPanelSection === null) {
                state.financialPanelSection = 'overview'
            }
        },
        setFinancialPanelSection(state, action: PayloadAction<string | null>) {
            state.financialPanelSection = action.payload
        },
    }
})

const { actions, reducer } = homeSlice

export const {
    updatePrimarySecurity,
    setResolvedPrimarySecurity,
    setOptionChainExpandedRows,
    toggleOptionChainExpandedRow,
    setOptionChainView,
    setSelectedOptionsExpiry,
    setActiveLayout,
    setLastActiveLayout,
    setSecuritySearchOpen,
    setFinancialPanelOpen,
    setFinancialPanelSection,
} = actions


export default reducer
